import React, { FC, useMemo } from 'react';
import { ButtonStroke } from '@blink/components';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonSize } from '@blink/components/src/Button/Button.types';
import { shortFormatNumber } from '@blink/components/src/utils';
import { useParams } from 'react-router-dom';

import { useUserSuppliesRowsStyle } from './UserSuppliesRows.style';

import { ModalName } from '@/shared/store/modals/constants';
import { showModalAction } from '@/shared/store/modals/actions';
import { Store } from '@/shared/store';
import { getVaultPools } from '@/shared/store/vaults/actions';
import { Vault } from '@/shared/api/vault';
import { formatSmart, formatWithCommas } from '@/shared/utils/helpers';
import { AssetWithIcon } from '@/shared/ui';

type UserSuppliesRowsProps = {
    vault: Vault;
    items: Vault[];
};

export const UserSuppliesRows: FC<UserSuppliesRowsProps> = React.memo(({ items, vault }) => {
    const classes = useUserSuppliesRowsStyle();
    const dispatch = useDispatch();
    const { address } = useParams();

    const { marginAccounts, vaults } = useSelector((state: Store) => ({
        marginAccounts: state.userVaults.marginAccounts,
        vaults: state.vaults.vaults,
    }));

    const currentAccount = useMemo(
        () => marginAccounts?.find((account) => account.agreementAddress === vault?.address),
        [marginAccounts, vault?.address],
    );

    const refreshLinkedPools = async () =>
        dispatch(
            getVaultPools({
                vaultAddress: vault.address,
                marginAccountAddress: currentAccount?.address as string,
            }),
        );

    if (!items || !currentAccount?.address) {
        return null;
    }

    return (
        <>
            {items.map((item, index) => {
                const agreement = vaults.find((agr) => agr.address === address);

                const handleWithdraw = () => {
                    dispatch(
                        showModalAction({
                            modalName: ModalName.MARGIN_ACCOUNTS_VAULT_WITHDRAWAL_MODAL,
                            modalTitle: 'Withdraw Liquidity',
                            modalProps: {
                                currentApy: Number(item.apy),
                                totalDeposited: item.totalDeposited,
                                totalDepositThreshold: shortFormatNumber({
                                    amount: Number(item.totalDepositThreshold),
                                }),
                                isWithdraw: true,
                                agreementAddress: item.address,
                                tokenAddress: agreement?.leverage?.address,
                                tokenSymbol: item?.leverage.symbol,
                                tokenDecimals: agreement?.leverage.decimals,
                                marginAccountAddress: currentAccount?.address,
                                interestClaimable: item!.interestClaimable,
                                totalPosition: item!.totalPosition,
                                interestAccrued: item!.interestAccrued,
                                poolUtilization: item!.poolUtilization,
                                poolBalance: item!.poolBalance,
                                refresh: refreshLinkedPools,
                                netApy: item!.netApy,
                            },
                        }),
                    );
                };
                return (
                    <tr key={index} className={classes.tr}>
                        <td className={classes.firstTd}>{item?.title}</td>
                        <td>
                            <AssetWithIcon assetName={item?.leverage.symbol || 'DAI'} />
                        </td>
                        <td>
                            <div>{formatWithCommas(item.totalPosition || '')}</div>
                        </td>
                        <td>{item.apy}%</td>
                        <td>
                            <div className={classes.rewards}>
                                <div>
                                    {formatSmart(item.interestAccrued!)} {item?.leverage.symbol}
                                </div>
                            </div>
                        </td>
                        <td className={classes.buttonTd}>
                            <ButtonStroke
                                size={ButtonSize.md}
                                className={classes.button}
                                isPrimary
                                onClick={handleWithdraw}
                            >
                                Withdraw
                            </ButtonStroke>
                        </td>
                    </tr>
                );
            })}
        </>
    );
});
